import { useEffect, useState } from "react";
import GoogleAd from "./GoogleAd";
import AdCreative from "./AdCreative";
import { cardImageLoader } from "../../utils/imageLoader";
import type { BannerAd } from "../../utils/homeData";

const HORIZONTAL_SLOTS: Record<string, string> = {
  TOP: "5811344272",
  BOTTOM: "3235961441",
};

const MOBILE_SQUARE_SLOTS: Record<string, string> = {
  TOP: "2587469309",
  BOTTOM: "3689739937",
};

const MOBILE_SQUARE_DIMENSIONS = {
  width: 300,
  height: 250,
} as const;

// The AdSense slot's footprint, known before any JavaScript runs: the 300x250
// square below `md`; from `md` up the responsive "auto" unit, which Google
// renders 280px tall in a container this wide (measured on the live TOP slot —
// the old 90px minimum was what the <ins> asked for, not what came back, and
// the 90→280 growth was itself a 0.08 shift for consenting readers). Rendered on
// the server so the box exists at first paint and nothing moves when the <ins>
// mounts after hydration — that mount was the article page's biggest layout
// shift (0.11 of 0.18). For visitors who have not accepted advertising cookies
// AdSense never loads, so `.ad-slot-reserved` collapses to nothing for them via
// the `html[data-ads]` attribute the inline script in _document.tsx sets from the
// stored consent (globals.scss). Unfilled/blocked slots still collapse through
// onAdFailed, exactly as before.
const RESERVED_CLASS = "ad-slot-reserved w-full min-h-[250px] md:min-h-[280px]";

const useIsMobile = () => {
  const [viewport, setViewport] = useState<{
    isMobile: boolean;
    isTablet: boolean;
  } | null>(null);

  useEffect(() => {
    const check = () =>
      setViewport({
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
      });
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return viewport;
};

type Props = {
  type: string;
  subLocation: string;
  forceGoogle?: boolean;
  mock?: string;
  /**
   * The backend banner for this slot as fetched by the page's getStaticProps
   * (utils/homeData.ts fetchBannerAd): an ad renders on the server, `null` means
   * "the backend has none, use the Google fallback", `undefined` keeps the old
   * client fetch. The client-only version painted the banner ~400 ms after
   * hydration below content that had already rendered — a 0.03–0.19 layout
   * shift and, on /news desktop, an LCP element discovered late.
   */
  initialAd?: BannerAd | null;
  /** Banner image loading; defaults to eager for TOP slots (near the top of list pages). */
  imageLoading?: "eager" | "lazy";
  /** Beside its square (BannerRow): the responsive classes that make the banner fill the row's height. */
  fillClassName?: string;
};

const HorizontalAds = (props: Props) => {
  const seeded = props.initialAd !== undefined;
  const [ad, setAd] = useState<BannerAd | null>(props.initialAd ?? null);
  const [loaded, setLoaded] = useState(seeded);
  const [fallbackAdHidden, setFallbackAdHidden] = useState(false);
  const viewport = useIsMobile();
  const useBackendAds = !props.forceGoogle;
  const position = props.subLocation === "BOTTOM" ? "BOTTOM" : "TOP";

  useEffect(() => {
    setFallbackAdHidden(false);

    if (!useBackendAds) {
      setAd(null);
      setLoaded(true);
      return;
    }
    if (seeded) return;

    const fetchAds = async () => {
      try {
        const res = await fetch(
          `${process.env.BACKEND_BASE_URL}/ads?location=${props.type}&subLocation=${props.subLocation}&type=BANNER&order=FIRST`
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const firstBanner = data.data.find((item: any) => Boolean(item?.thumbnail));
          setAd(firstBanner ?? null);
        } else {
          setAd(null);
        }
      } catch (err) {
        console.error("Fetch banner ads error:", err);
        setAd(null);
      } finally {
        setLoaded(true);
      }
    };
    fetchAds();
  }, [props.type, props.subLocation, useBackendAds, seeded]);

  const getUrl = (url: string | null) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `https://${url}`;
  };

  // Backend banner: renders on the server when seeded (no viewport needed).
  if (useBackendAds && ad?.thumbnail) {
    // A fixed 4:1 frame (AdCreative): the box is reserved before the image
    // arrives and a creative in another shape is shown whole, never cropped.
    return (
      <AdCreative
        src={ad.thumbnail}
        href={getUrl(ad.url)}
        title={ad.title}
        shape="banner"
        loader={cardImageLoader}
        sizes="(max-width: 768px) 100vw, 810px"
        loading={props.imageLoading ?? (props.subLocation === "TOP" ? "eager" : "lazy")}
        fillClassName={props.fillClassName}
      />
    );
  }

  if (!loaded || !viewport) {
    // Server render and the first client frame: the Google slot's box, empty.
    // An unseeded backend slot has no fixed size yet, so it still appears on load.
    return useBackendAds && !seeded ? null : <div className={RESERVED_CLASS} aria-hidden="true" />;
  }

  // No backend banner → Google AdSense fallback
  if (fallbackAdHidden) return null;

  const isMobileSquare = viewport.isMobile;
  const adSlot = isMobileSquare ? MOBILE_SQUARE_SLOTS[position] : HORIZONTAL_SLOTS[position];
  const adStyle = isMobileSquare
    ? {
        display: "inline-block",
        width: `${MOBILE_SQUARE_DIMENSIONS.width}px`,
        height: `${MOBILE_SQUARE_DIMENSIONS.height}px`,
      }
    : {
        display: "block",
        width: "100%",
        minHeight: "90px",
      };

  return (
    <div
      className={`${RESERVED_CLASS} flex justify-center ${
        isMobileSquare ? "side-ad-container" : "banner-ad-container"
      }`}
    >
      <div className={isMobileSquare ? "w-full max-w-[300px] mx-auto text-center" : "w-full"}>
        <GoogleAd
          key={`${position}-${adSlot}-${isMobileSquare ? "mobile" : "desktop"}`}
          client="ca-pub-6015892276154863"
          slot={adSlot}
          style={adStyle}
          format="auto"
          responsive={isMobileSquare ? "false" : "true"}
          onAdFailed={() => setFallbackAdHidden(true)}
        />
      </div>
    </div>
  );
};

export default HorizontalAds;
